import React, { useState } from 'react';

import Button from '@atoms/new/button';
import { InputWithSuggestions } from '@atoms/InputWithSuggestions';
import MaxLengthCounter from '@components/Presentation/atoms/MaxLengthCounter';

import SettingBlock from '@components/Presentation/main/Settings/units/SettingBlock';

import { GenerateFeatureSuggestionsModal } from '@components/Presentation/ai/generate-feature-suggestions-modal';
import { calculateRemainingChars } from '@components/Presentation/helpers';

import PowerPollOptions from './PowerPollsOptions';
import MultipleChoiceOptions from './MultipleChoiceOptions';
import ChoiceCircleOptions from './ChoiceCircleOptions';
import { getQuestionsByCategoryAndSub } from '@lib/question-suggestions';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import QuizOptions from './QuizOptions';
import QuizImageUploader from './QuizImageUploader';
import { SparkleIcon as AutoAwesomeRoundedIcon } from '@phosphor-icons/react';

const InteractionQuestion = ({
  featureType,
  questionLabel = 'Heading',
  title,
  setTitle,
  options,
  setOptions,
  addNewOption,
  deleteOption,
  setting,
  updateSetting,
  showGenerateWithAIButton = true
}) => {
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [showGenerateWithAIModal, setShowGenerateWithAIModal] = useState(false);
  const user = useTeamsData((s) => s.user);

  const isFeatureTypeSupported = () => ['pollWithOptions', 'wordCloud', 'talkingTiles', 'transientThought', 'multipleChoice', 'quiz'].includes(featureType);

  const handleChangeOptionBody = (index, event) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      body: event.target.value
    };
    setOptions(updatedOptions);
  };

  const handleChangeOptionCheck = (index, e, prevIndex) => {
    const updatedOptions = [...options];

    if (prevIndex !== undefined) {
      updatedOptions[prevIndex] = {
        ...updatedOptions[prevIndex],
        checked: false
      };
    }

    updatedOptions[index] = {
      ...updatedOptions[index],
      checked: e.target.value
    };
    setOptions(updatedOptions);
  };

  const [suggestions] = useState(
    getQuestionsByCategoryAndSub(
      featureType,
      user?.user_detail?.onboarding?.sessions
    )
  );

  return <>
    {showGenerateWithAIButton && isFeatureTypeSupported() && <Button
      className='mt-4'
      fullWidth
      kind="secondary"
      size="sm"
      onClick={() => setShowGenerateWithAIModal(true)}
      startContent={<AutoAwesomeRoundedIcon size={20} className='text-primary-500' />}
    >
      <span style={{
        background: 'linear-gradient(90deg, #FF0064 0%, #0086FF 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>Generate with AI</span>
    </Button>}

    <SettingBlock
      title={questionLabel}
      hasBorderTop={false}
      addPadding={false}
      defaultExpanded={true}
      tooltip={
        <p>
          Add a prompt—such as a question <br /> or instruction—for your
          audience.
        </p>
      }
    >
      <InputWithSuggestions
        placeholder="Type a prompt or question..."
        value={title}
        autoFocus
        suggestions={suggestions}
        onChange={(e) => setTitle(e.target.value)}
        handleSelectSuggestion={(suggestion) => {
          setTitle(suggestion.question);
          if (suggestion.options) {
            setOptions(suggestion.options);
          }

          if (suggestion.feature === 'ratingPolls') {
            if (suggestion.setting?.ratingPollScale) {
              updateSetting('ratingPollScale', suggestion.setting.ratingPollScale);
            }

            if (suggestion.setting?.lowScoreLabel) {
              updateSetting('lowScoreLabel', suggestion.setting.lowScoreLabel);
            }

            if (suggestion.setting?.highScoreLabel) {
              updateSetting('highScoreLabel', suggestion.setting.highScoreLabel);
            }
          }
        }}
        size="lg"
        maxLength={255}
        handleFocus={setIsTitleFocused}
        addOnRight={
          isTitleFocused ? (
            <MaxLengthCounter
              value={calculateRemainingChars(title)}
            />
          ) : null
        }
      />
      {calculateRemainingChars(title) === 0 && (
        <p className="text-sm text-alert mt-1">
          You have reached the maximum length
        </p>
      )}
    </SettingBlock>

    {(featureType === 'multipleChoice') && <MultipleChoiceOptions
      options={options}
      setting={setting}
      updateSetting={updateSetting}
      handleChangeOptionBody={handleChangeOptionBody}
      handleChangeOptionCheck={handleChangeOptionCheck}
      addNewOption={addNewOption}
      deleteOption={deleteOption}
    />}

    {featureType === 'quiz' && <QuizImageUploader setting={setting} updateSetting={updateSetting} />}

    {(featureType === 'quiz') && <QuizOptions
      options={options}
      setting={setting}
      updateSetting={updateSetting}
      handleChangeOptionBody={handleChangeOptionBody}
      handleChangeOptionCheck={handleChangeOptionCheck}
      addNewOption={addNewOption}
      deleteOption={deleteOption}
    />}

    {(featureType === 'pollWithOptions') && <PowerPollOptions
      options={options}
      handleChangeOptionBody={handleChangeOptionBody}
      addNewOption={addNewOption}
      deleteOption={deleteOption}
    />}

    {(featureType === 'wheel') &&
      <ChoiceCircleOptions
        options={options}
        setOptions={setOptions}
      />}

    <GenerateFeatureSuggestionsModal
      setQuestionTitle={(value) => setTitle(value)}
      setOptions={(options) => options.length && setOptions(options.map((opt, index) => ({ ...opt, option_number: index + 1 })))}
      feature={featureType}
      isOpen={showGenerateWithAIModal}
      onClose={() => setShowGenerateWithAIModal(false)}
    />
  </>;
};

export default InteractionQuestion;
